import React, { useState, useEffect, useRef, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import dayjs from 'dayjs';

import { List, Col, Button, Dropdown, Typography, Row, Modal, Badge, Popover, Space, Select, Form, App } from 'antd';
import {
  ClockCircleOutlined,
  RollbackOutlined,
  DownOutlined,
  LinkOutlined,
  ExclamationCircleOutlined,
  BellOutlined,
  PushpinFilled,
  PushpinOutlined,
} from '@ant-design/icons';

import Comments from './comments';
import ReAssignModal from '../re-assign-modal';

import { siteUrls } from '../../../shared/constants/siteUrls';
import { NoteOwnerTypes, fileOwnerTypes, TruckingCompanyTypes, stickyNoteColor } from '../../../shared/constants/enums';
import { IFrame } from '../../../shared/components/iframe';

import * as actions from '../store/actions';

import NoteFilesModal from '../note-files-modal';
import AssignNoteToTaskModal from '../note-assign-task-modal/index';
import { NoteFileAssociation, Note } from '../../../shared/models';

import '../styles.less';

const { Text } = Typography;

interface ControlConfig {
  isMomentumEnabled: boolean;
}

export type NoteProps = {
  item: Note;
  fileAssociations?: NoteFileAssociation[];
  refresh: () => void;
  refreshAfterArchiveOrReassign: (archive: boolean, response?: any) => void;
  compactModeEnabled?: boolean;
  showArchiveEnabled?: boolean;
  baseRequestParams: any;
  ownerType: NoteOwnerTypes | undefined;
  insuredId?: string | null | undefined;
  agencyId?: string | null | undefined;
  policyId?: string | null | undefined;
  taskId?: string | null | undefined;
  agentId?: string | null | undefined;
  isHistoryNotes?: boolean;
  isSuperUserNotes?: boolean;
  opportunityId?: string | null | undefined;
  claimId?: string | null | undefined;
  serviceRequestId?: string | null | undefined;
  searchString?: string | null | undefined;
};

interface RootState {
  notes: {
    addNoteModalOpened: boolean;
    archiveNoteLoading: boolean;
    toggleNotePinLoading: boolean;
    controlConfig: ControlConfig;
  };
}

const NoteComponent: FC<NoteProps> = ({
  item,
  fileAssociations,
  refresh,
  refreshAfterArchiveOrReassign,
  compactModeEnabled,
  showArchiveEnabled,
  baseRequestParams,
  ownerType,
  insuredId,
  agencyId,
  policyId,
  taskId,
  agentId,
  isHistoryNotes,
  isSuperUserNotes,
  opportunityId,
  claimId,
  serviceRequestId,
  searchString,
}) => {
  const { modal } = App.useApp();

  const [remindersInfo, setRemindersInfo] = useState<string[]>([]);
  const [comments, setComments] = useState<Note[]>(item.comments);
  const [reassignModalVisible, setReassignModalVisibility] = useState<boolean>(false);
  const [noteFilesModalVisible, setNoteFilesModalVisible] = useState<boolean>(false);
  const [assignNoteToTaskModalVisible, setAssignNoteToTaskModalVisible] = useState<boolean>(false);
  const [togglePinButtonLoadingId, setTogglePinButtonLoadingId] = useState<string | null>(null);

  const addNoteModalOpened = useSelector((state: RootState) => state.notes.addNoteModalOpened);
  const archiveNoteLoading = useSelector((state: RootState) => state.notes.archiveNoteLoading);
  const toggleNotePinLoading = useSelector((state: RootState) => state.notes.toggleNotePinLoading);
  const controlConfig = useSelector((state: RootState) => state.notes.controlConfig);

  const dispatch = useDispatch();

  const messageRef = useRef<HTMLDivElement>(null);

  const [colorStickForm] = Form.useForm();

  useEffect(() => {
    setComments(item.comments);
  }, [item.comments]);

  useEffect(() => {
    if (!!item.reminderSentToCustomEMail) {
      setRemindersInfo(item.reminderSentToCustomEMail.split(','));
    }
  }, [item]);

  useEffect(() => {
    if (addNoteModalOpened) {
      cancelAddComment();
    }
  }, [addNoteModalOpened]);

  const addComment = (): void => {
    if (!comments.some((item) => item.state === 'new')) {
      const newComment: Note = {
        subject: '',
        reminderDateTime: null,
        reminderSendToEMails: '',
        reminderSentToCustomEMail: '',
        state: 'new',
      } as Note;

      const updatedComments = [...comments];

      updatedComments.unshift(newComment);
      setComments(updatedComments);
    }
  };

  const cancelAddComment = (): void => {
    const updatedComments = [...comments];
    const indexOfItemToDelete = updatedComments.findIndex((item) => item.state === 'new');

    if (indexOfItemToDelete !== -1) {
      updatedComments.splice(indexOfItemToDelete, 1);
      setComments(updatedComments);
    }
  };

  const showDeleteConfirm = (id: string): void => {
    modal.confirm({
      title: 'Are you sure archive this note?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      confirmLoading: archiveNoteLoading,
      okButtonProps: { disabled: archiveNoteLoading },
      onOk() {
        return handleArchiveNoteChange(id, true);
      },
    } as any);
  };

  const badgeColorsOpts: Record<number, string> = {
    1: 'orange',
    2: 'yellow',
    3: 'green',
    4: 'blue',
    5: 'geekblue',
    6: 'purple',
    7: 'pink',
  };

  const showStickConfirm = (id: string): void => {
    colorStickForm.resetFields();

    modal.confirm({
      title: 'Make Note Sctiky',
      okText: 'Save',
      cancelText: 'Close',
      content: (
        <Form name="colorStickForm" form={colorStickForm}>
          <Form.Item
            name="stickyNoteColor"
            rules={[{ required: true, message: 'Please choose a color to save or press Cancel if you dont want to proceed.' }]}
          >
            <Select allowClear placeholder="Choose background color" style={{ width: '285px' }}>
              {stickyNoteColor.map((item) => (
                <Select.Option key={item.key} value={item.key}>
                  <Badge size="default" color={badgeColorsOpts[item.key]} /> {item.value}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      ),
      onOk: async () => {
        try {
          const value = await colorStickForm.validateFields();

          dispatch(actions.updateNote({ NoteId: id, StickyNoteColor: value.stickyNoteColor }) as any).then((res: any) => {
            if (res) {
              document.dispatchEvent(
                new CustomEvent('NCM_STICKY_NOTE_CREATED', {
                  detail: {},
                }),
              );

              refresh();
            }
          });

          return Promise.resolve();
        } catch (error) {
          return Promise.reject();
        }
      },
    });
  };

  const showAssignTaskModal = (): void => {
    setAssignNoteToTaskModalVisible(true);
  };

  const handleArchiveNoteChange = (id: string, archive: boolean) => {
    return dispatch(actions.archiveNote(id, archive) as any).then((res: any) => refreshAfterArchiveOrReassign(archive, res));
  };

  const handlePinNoteChange = (id: string, isPinned: boolean) => {
    setTogglePinButtonLoadingId(id);

    return dispatch(actions.toggleNotePin(id, !isPinned) as any).then((res: any) => {
      if (res) {
        refresh();
      }

      setTogglePinButtonLoadingId(null);
    });
  };

  const reassignModalClose = (shouldRefresh: boolean | undefined): void => {
    setReassignModalVisibility(false);

    if (shouldRefresh) {
      refreshAfterArchiveOrReassign(true);
    }
  };

  const noteFilesModalClose = (): void => {
    setNoteFilesModalVisible(false);

    refresh();
  };

  const assignNoteToTaskModalClose = (): void => {
    setAssignNoteToTaskModalVisible(false);
    refresh();
  };

  const showNoteFilesModal = (noteId: string): void => {
    let fileOwnerType = fileOwnerTypes.InsuredNote;

    switch (ownerType) {
      case NoteOwnerTypes.Insured:
      case NoteOwnerTypes.Policy:
      case NoteOwnerTypes.Task:
      case NoteOwnerTypes.Claim:
        fileOwnerType = fileOwnerTypes.InsuredNote;
        break;
      case NoteOwnerTypes.Agency:
        fileOwnerType = fileOwnerTypes.AgencyNote;
        break;
      case NoteOwnerTypes.Agent:
        fileOwnerType = fileOwnerTypes.AgentNote;
        break;
      case NoteOwnerTypes.Opportunity:
        fileOwnerType = fileOwnerTypes.OpportunityNote;
        break;
    }

    (window as any)['ncFileManager'] = {
      ['insuredId']: insuredId,
      ['noteId']: noteId,
      ['agencyId']: agencyId,
      ['policyId']: policyId,
      ['taskId']: taskId,
      ['agentId']: agentId,
      ['ownerType']: fileOwnerType,
      ['opportunityId']: opportunityId,
      ['claimId']: claimId,
      ['serviceRequestId']: serviceRequestId,
    };

    setNoteFilesModalVisible(true);
  };

  const getTruckingCompanyTypeLabel = (type: number): string => {
    switch (type) {
      case TruckingCompanyTypes.Insured:
        return 'Insured';
      case TruckingCompanyTypes.Prospect:
        return 'Prospect';
      case TruckingCompanyTypes.MGA:
        return 'MGA';
      case TruckingCompanyTypes.Carrier:
        return 'Carrier';
      case TruckingCompanyTypes.FinanceCompany:
        return 'Finance Company';
      case TruckingCompanyTypes.ReferralSource:
        return 'Referral Source';
      case TruckingCompanyTypes.Other:
        return 'Other Contact';
      default:
        return 'Insured';
    }
  };

  const isFileAttached = (noteId: string): boolean => {
    if (fileAssociations && fileAssociations.length) {
      const association = fileAssociations.find((x) => x.noteId === noteId);

      if (association) {
        return true;
      }
    }

    return false;
  };

  const menuItems = [
    ...(!isSuperUserNotes
      ? [
        {
          label: (
            <a
              href={siteUrls.getTaskCreateUrl(
                controlConfig.isMomentumEnabled,
                item.id,
                insuredId,
                item.notePolicies ? item.notePolicies.map((x) => x.policyId) : [],
                false,
              )}
            >
              Create Task
            </a>
          ),
          key: '2',
        },
      ]
      : []),
    ...(!isSuperUserNotes
      ? [
        {
          label: <a href={siteUrls.getServiceRequestCreateUrl(controlConfig.isMomentumEnabled, item.id, insuredId)}>Create Service Request</a>,
          key: '3',
        },
      ]
      : []),
    ...(!isSuperUserNotes
      ? [
        {
          label: <a href={siteUrls.getCreateToDoUrl(controlConfig.isMomentumEnabled, item.id, insuredId)}>Create To-Do</a>,
          key: '4',
        },
      ]
      : []),
    // todo: remove ternary expression when files for Claims and Service Requests notes will be in place
    ...(!isSuperUserNotes && !isHistoryNotes && ownerType !== NoteOwnerTypes.ServiceRequest
      ? [
        {
          label: 'Upload Files',
          key: '5',
          onClick: () => showNoteFilesModal(item.id),
        },
      ]
      : []),
    // ...ownerType === NoteOwnerTypes.Insured ? [{
    //   label: 'Re-assign',
    //   key: '6',
    //   onClick: () => setReassignModalVisibility(true)
    // }] : [],
    {
      label: 'Archive',
      key: '7',
      onClick: () => showDeleteConfirm(item.id),
    },
    ...(!isSuperUserNotes && ownerType !== NoteOwnerTypes.Task
      ? [
        {
          label: 'Assign to Task',
          onClick: () => showAssignTaskModal(),
          key: '8',
        },
      ]
      : []),
    ...(item.color === 0
      ? [
        {
          label: 'Make Sticky',
          key: '9',
          onClick: () => showStickConfirm(item.id),
        },
      ]
      : []),
  ];

  return (
    <>
      <Badge.Ribbon text="Archived" placement="start" color="red" style={{ top: '-11px', display: !item.hide ? 'none' : '' }}>
        <List.Item className="note-list-item">
          <Row className="note-row">
            <Col span={24}>
              <Row>
                <Col flex="auto">
                  <div style={{ maxWidth: '850px' }}>
                    <span className="note-owner">{item.lastChangeUserDisplayName || ''}</span>
                    <span className="note-timestamp" title="Created At">
                      {' '}
                      <ClockCircleOutlined /> {dayjs(item.createDate).format('MMM DD, YYYY, hh:mm:ss A')}{' '}
                    </span>
                    {item.reminderDateTime ? (
                      <>
                        <span className="note-reminder" title="Reminder">
                          {' '}
                          <BellOutlined /> {dayjs(item.reminderDateTime).format('MMM DD, YYYY, hh:mm:ss A')}
                        </span>
                        <Popover
                          className="note-reminder-popover"
                          title={
                            <Space direction="horizontal">
                              <Text strong>Reminder date:</Text>
                              <Text>{dayjs(item.reminderDateTime).format('MMM DD, YYYY, hh:mm:ss A')}</Text>
                            </Space>
                          }
                          content={
                            <Space direction="horizontal">
                              <Text strong>Sent to emails:</Text>
                              <Space direction="vertical">
                                {remindersInfo &&
                                  remindersInfo.map((email, index) => (
                                    <Text className="note-reminder-popover-email" key={index}>
                                      {email}
                                    </Text>
                                  ))}
                              </Space>
                            </Space>
                          }
                          trigger="hover"
                          placement="right"
                        >
                          <ExclamationCircleOutlined />
                        </Popover>
                      </>
                    ) : null}
                    {item.originString ? (
                      <span className="note-type">
                        {' '}
                        <Text>Origin: {item.originString}</Text>
                      </span>
                    ) : null}
                    {item.noteType ? (
                      <span className="note-type">
                        {' '}
                        <Text>Type: {item.noteType}</Text>
                      </span>
                    ) : null}
                    {item.disposition ? (
                      <span className="note-type">
                        {' '}
                        <Text>Disposition: {item.disposition}</Text>
                      </span>
                    ) : null}
                    {item.sentSmsFromPhone ? (
                      <span className="note-type">
                        {' '}
                        <Text>Sent SMS from: {item.sentSmsFromPhone} </Text>
                      </span>
                    ) : null}
                    {item.sentSmsToPhone ? (
                      <span className="note-type">
                        {' '}
                        <Text>Sent SMS to: {item.sentSmsToPhone} </Text>
                      </span>
                    ) : null}
                    {item.insuredName ? (
                      <span className="note-type">
                        {' '}
                        <Text>
                          {getTruckingCompanyTypeLabel(item.truckingCompanyType!)}:{' '}
                          <a
                            href={siteUrls.getTruckingCompanyDetailsUrl(
                              controlConfig.isMomentumEnabled,
                              item.truckingCompanyId,
                              item.truckingCompanyType,
                            )}
                          >
                            {item.insuredName}
                          </a>
                        </Text>
                      </span>
                    ) : null}
                    {item.policyNumber || (item.notePolicies && item.notePolicies.length) ? (
                      <span className="note-type">
                        {' '}
                        {!!item.notePolicies && item.notePolicies.length > 0 ? (
                          <Text>
                            {item.notePolicies.length > 1 ? 'Policies:' : 'Policy:'}{' '}
                            {item.notePolicies.map((p, index) => (
                              <a key={index} href={siteUrls.getPolicyDetailsUrl(controlConfig.isMomentumEnabled, p.policyId, p.isQuote)}>
                                {p.policyNumber}
                                {item.notePolicies ? (index < item.notePolicies.length - 1 ? ', ' : ' ') : ''}
                              </a>
                            ))}
                          </Text>
                        ) : item.policyNumber ? (
                          <Text>
                            Policy: <a href={siteUrls.getPolicyDetailsUrl(controlConfig.isMomentumEnabled, item.policyId, false)}>{item.policyNumber}</a>
                          </Text>
                        ) : null}
                      </span>
                    ) : null}
                    {item.ticketTitle ? (
                      <span className="note-type">
                        {' '}
                        <Text>
                          Task: <a href={siteUrls.getTaskDetailsUrl(controlConfig.isMomentumEnabled, item.ticketId)}>{item.ticketTitle}</a>
                        </Text>
                      </span>
                    ) : null}
                    {item.claimNumber ? (
                      <span className="note-type">
                        {' '}
                        <Text>
                          Claim: <a href={siteUrls.getClaimDetailsUrl(controlConfig.isMomentumEnabled, item.claimId)}>{item.claimNumber}</a>
                        </Text>
                      </span>
                    ) : null}
                    {isFileAttached(item.id) && !isHistoryNotes ? (
                      <a className="note-attachment" onClick={() => showNoteFilesModal(item.id)}>
                        <LinkOutlined />
                      </a>
                    ) : null}
                  </div>

                  <div className="note-subject" ref={messageRef}>
                    {compactModeEnabled ? (
                      <div className="note-subject-compact">{item.subjectWithoutHTML}</div>
                    ) : (
                      <IFrame
                        frameBorder="0"
                        styleSelector='link[href*="React_Sources"], link[href*="react-sources-cdn"]'
                        scrolling="yes"
                        className="iframe-subject"
                      >
                        <div dangerouslySetInnerHTML={{ __html: item.subject }} />
                      </IFrame>
                    )}
                  </div>
                </Col>

                <Col flex="230px" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {item.hide ? (
                    <Button
                      style={{ marginRight: '10px' }}
                      type="primary"
                      ghost
                      icon={<RollbackOutlined />}
                      size="small"
                      onClick={() => handleArchiveNoteChange(item.id, false)}
                    >
                      Unarchive
                    </Button>
                  ) : (
                    <>
                      <Button
                        style={{ marginRight: '10px', fontSize: '12px' }}
                        type="text"
                        title={item.isPinned ? 'Unpin' : 'Pin'}
                        loading={toggleNotePinLoading && togglePinButtonLoadingId === item.id}
                        icon={item.isPinned ? <PushpinFilled rotate={-45} /> : <PushpinOutlined />}
                        size="small"
                        onClick={() => handlePinNoteChange(item.id, item.isPinned)}
                      ></Button>

                      <Button
                        style={{ marginRight: '10px', fontSize: '12px' }}
                        type="primary"
                        ghost
                        icon={<RollbackOutlined />}
                        size="small"
                        onClick={() => addComment()}
                      >
                        Add Comment
                      </Button>

                      <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                        <Button type="primary" ghost size="small" style={{ marginRight: '10px', fontSize: '12px' }}>
                          Actions <DownOutlined />
                        </Button>
                      </Dropdown>
                    </>
                  )}
                </Col>
              </Row>

              <Row className="comments-container">
                {comments && comments.length ? (
                  <Comments
                    parentNoteId={item.id}
                    items={comments}
                    showArchiveEnabled={showArchiveEnabled}
                    compactModeEnabled={compactModeEnabled}
                    cancelAddComment={cancelAddComment}
                    refresh={refresh}
                    baseRequestParams={baseRequestParams}
                    ownerType={ownerType}
                    insuredId={insuredId}
                    isSuperUserNotes={isSuperUserNotes}
                    controlConfig={controlConfig}
                    searchString={searchString}
                    notePolicies={item.notePolicies}
                  />
                ) : null}
              </Row>
            </Col>
          </Row>
        </List.Item>
      </Badge.Ribbon>

      {reassignModalVisible ? (
        <ReAssignModal
          visible={reassignModalVisible}
          close={(shouldRefresh) => reassignModalClose(shouldRefresh)}
          noteId={item.id}
        />
      ) : null}

      {noteFilesModalVisible ? (
        <NoteFilesModal visible={noteFilesModalVisible} refresh={() => noteFilesModalClose()} />
      ) : null}

      {assignNoteToTaskModalVisible ? (
        <AssignNoteToTaskModal
          visible={assignNoteToTaskModalVisible}
          insuredId={insuredId}
          note={item}
          close={() => setAssignNoteToTaskModalVisible(false)}
          refresh={() => assignNoteToTaskModalClose()}
        />
      ) : null}
    </>
  );
};

export default NoteComponent;
